import { useState } from 'react';
import { Loader2, X } from 'lucide-react';

import type { AssessTtqMultiSolutionWorkbenchResponse } from '@kelilearning/psycheflow-contracts';

import { formatTtqModelAuditDisplay, getTtqImageModelIcon } from '../../utils/ttqModelBrand';
import { candidateNeedsDiagram } from '../../utils/ttqDiagramInference';

type ImageModelOption = { id: string; label: string };

type TtqMultiSolutionAssessModalProps = {
  open: boolean;
  assessing: boolean;
  generating: boolean;
  assessment: AssessTtqMultiSolutionWorkbenchResponse | null;
  assessError: string | null;
  assessErrorDetail?: string | null;
  selectedIndexes: number[];
  imageModels: ImageModelOption[];
  imageModel: string;
  onImageModelChange: (model: string) => void;
  onToggleIndex: (index: number) => void;
  onClose: () => void;
  onRetryAssess: () => void;
  onConfirmGenerate: () => void;
};

export function TtqMultiSolutionAssessModal({
  open,
  assessing,
  generating,
  assessment,
  assessError,
  assessErrorDetail = null,
  selectedIndexes,
  imageModels,
  imageModel,
  onImageModelChange,
  onToggleIndex,
  onClose,
  onRetryAssess,
  onConfirmGenerate,
}: TtqMultiSolutionAssessModalProps) {
  const [detailOpen, setDetailOpen] = useState(false);

  if (!open) {
    return null;
  }

  const availableCandidates = assessment?.candidates.filter(
    (item) => !item.alreadyGenerated && !item.isSourceParticleMethod,
  ) || [];
  const canGenerate = Boolean(
    assessment?.hasMultipleSolutions
    && availableCandidates.length > 0
    && selectedIndexes.length > 0,
  );

  const modelDisplay = formatTtqModelAuditDisplay(assessment?.modelAudit);
  const selectedCandidates = assessment?.candidates.filter((item) => selectedIndexes.includes(item.index)) || [];
  const needsDiagram = selectedCandidates.some((item) => candidateNeedsDiagram(item));
  const imageModelLabel = imageModels.find((item) => item.id === imageModel)?.label || imageModel;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-card">
        <h3 className="text-lg font-semibold text-ink">一题多解 · 可行性评估</h3>
        <p className="mt-1 text-xs text-muted">先评估解法空间，可多选路径一并生成。</p>
        {modelDisplay ? (
          <p className="mt-2 flex items-center gap-2 text-xs text-muted">
            {modelDisplay.iconUrl ? (
              <img src={modelDisplay.iconUrl} alt="" className="h-4 w-4 rounded-sm" />
            ) : null}
            <span>
              评估模型：<span className="font-medium text-ink">{modelDisplay.label}</span>
            </span>
          </p>
        ) : null}

        {assessing ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted">
            <Loader2 className="animate-spin text-primary" size={18} />
            正在分析题目解法空间…
          </div>
        ) : null}

        {assessError ? (
          <div className="mt-4 rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger">
            <p className="whitespace-pre-wrap">{assessError}</p>
            {assessErrorDetail ? (
              <button
                type="button"
                className="mt-2 inline-flex items-center gap-1 rounded border border-danger/35 px-2 py-0.5 text-[11px] font-medium text-danger hover:bg-danger/10"
                onClick={() => setDetailOpen(true)}
              >
                查看错误详情
              </button>
            ) : null}
          </div>
        ) : null}

        {assessment && !assessing ? (
          <div className="mt-4 space-y-4">
            <div className="rounded-lg border border-border bg-canvas p-3 text-sm text-ink whitespace-pre-wrap">
              {assessment.reasonMarkdown}
            </div>

            {assessment.hasMultipleSolutions ? (
              <>
                <p className="text-sm text-muted">
                  估计可构造约 {assessment.estimatedTotalSolutions ?? assessment.candidates.length} 种不同解法
                  {assessment.existingSolutionCount > 0
                    ? `（工作台已生成 ${assessment.existingSolutionCount} 种）`
                    : ''}
                </p>
                <ul className="space-y-2">
                  {assessment.candidates.map((candidate) => {
                    const disabled = Boolean(candidate.alreadyGenerated || candidate.isSourceParticleMethod);
                    const badge = candidate.isSourceParticleMethod
                      ? '源知识颗粒方法'
                      : candidate.alreadyGenerated
                        ? '已在工作台生成'
                        : '';
                    const selected = selectedIndexes.includes(candidate.index);
                    return (
                      <li key={candidate.index}>
                        <label
                          className={`flex cursor-pointer gap-3 rounded-lg border p-3 text-sm transition-colors ${
                            disabled
                              ? 'cursor-not-allowed border-border/60 bg-black/[0.02] opacity-60'
                              : selected
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/40'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            disabled={disabled || generating}
                            checked={selected}
                            onChange={() => onToggleIndex(candidate.index)}
                          />
                          <span>
                            <span className="font-medium text-ink">
                              {candidate.label}
                              {badge ? `（${badge}）` : ''}
                              {!badge && candidateNeedsDiagram(candidate) ? '（需配图）' : ''}
                            </span>
                            <span className="mt-1 block text-xs text-muted">{candidate.approachSummary}</span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                {needsDiagram ? (
                  <label className="flex flex-col gap-1 rounded-lg border border-border bg-canvas p-3 text-sm">
                    <span className="flex items-center gap-2 text-muted">
                      <img src={getTtqImageModelIcon()} alt="" className="h-4 w-4 rounded-sm" />
                      解法配图模型
                    </span>
                    <select
                      className="rounded-md border border-border bg-surface px-2 py-1.5 text-ink"
                      value={imageModel}
                      disabled={generating}
                      onChange={(e) => onImageModelChange(e.target.value)}
                    >
                      {imageModels.map((item) => (
                        <option key={item.id} value={item.id}>{item.label}</option>
                      ))}
                    </select>
                    <span className="text-xs text-muted">已选解法含图解/表上作业/运输表等可视化步骤，将用 {imageModelLabel} 生成配图。</span>
                  </label>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="rounded-lg border border-border px-4 py-2 text-sm text-ink hover:bg-black/5 disabled:opacity-60"
            onClick={onClose}
            disabled={generating}
          >
            关闭
          </button>
          {assessError || (assessment && !assessment.hasMultipleSolutions) ? (
            <button
              type="button"
              className="rounded-lg border border-border px-4 py-2 text-sm text-ink hover:bg-black/5"
              onClick={onRetryAssess}
              disabled={assessing || generating}
            >
              重新评估
            </button>
          ) : null}
          {assessment?.hasMultipleSolutions ? (
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60"
              onClick={onConfirmGenerate}
              disabled={!canGenerate || generating || assessing}
            >
              {generating ? (
                <>
                  <Loader2 className="animate-spin text-white" size={16} />
                  生成中…
                </>
              ) : `生成所选解法${selectedIndexes.length > 0 ? `（${selectedIndexes.length}）` : ''}`}
            </button>
          ) : null}
        </div>
      </div>

      {detailOpen && assessErrorDetail ? (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/45 p-4">
          <div className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="text-sm font-semibold text-ink">错误详情</p>
              <button type="button" className="text-muted hover:text-ink" onClick={() => setDetailOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap px-4 py-3 text-[11px] leading-relaxed text-ink">
              {assessErrorDetail}
            </pre>
            <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
              <button
                type="button"
                className="rounded-md border border-border px-3 py-1.5 text-xs text-muted hover:bg-black/[0.03]"
                onClick={() => setDetailOpen(false)}
              >
                关闭
              </button>
              <button
                type="button"
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white"
                onClick={() => {
                  void navigator.clipboard?.writeText(assessErrorDetail);
                }}
              >
                复制详情
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
